import cn from "classnames";

import { Loader, LoaderSizeProps } from "@/components/loader/loader.component";

import { brown } from "@/app/font";

/** Button Component */
export const Button = ({
  className,
  children,
  disabled,
  loading,
  loadingSize = "sm",
  loaderVariant,
  primary,
  secondary,
  border,
  onClick,
  ...rest
}: ButtonProps) => {
  return (
    <button
      data-testid="button"
      data-type={primary ? "primary" : secondary ? "secondary" : "default"}
      className={cn(
        {
          [`${brown.className} flex cursor-pointer items-center justify-center gap-2 rounded-full border px-6 py-3 font-bold uppercase duration-300 disabled:pointer-events-none disabled:select-none md:text-[18px]`]:
            primary || secondary,
          "bg-primary text-secondary disabled:bg-greyscale-strength hover:bg-primary-400 hover:border-secondary focus-visible:border-secondary focus-visible:ring-secondary border border-transparent focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:text-white":
            primary && !border,
          "border-primary text-primary disabled:border-greyscale-strength disabled:text-greyscale-strength focus-visible:ring-secondary border-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none":
            primary && border,
          "bg-secondary disabled:bg-greyscale-strength focus-visible:border-secondary-100 hover:border-secondary-100 hover:bg-secondary-300 focus-visible:ring-secondary border border-transparent text-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none":
            secondary && !border,
          "border-secondary text-secondary disabled:border-greyscale-strength disabled:text-greyscale-strength focus-visible:ring-secondary border-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:bg-transparent":
            secondary && border,
        },
        className,
      )}
      disabled={disabled || loading}
      onClick={onClick}
      {...rest}>
      {children}
      <Loader loading={loading} size={loadingSize} variant={loaderVariant} />
    </button>
  );
};

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Class overrides */
  className?: string;
  children?: React.ReactNode;
  /** Disable button */
  disabled?: boolean;
  /** Show loading indicator */
  loading?: boolean;
  /** Loading indicator size */
  loadingSize?: LoaderSizeProps;
  /** Class name for loader */
  loaderVariant?: "primary" | "secondary";
  /** Primary button theme */
  primary?: boolean;
  /** Secondary button theme */
  secondary?: boolean;
  /** Secondary button theme */
  border?: boolean;
  /** Click event handler */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Interest Invokers API — opens the popover on hover/focus for the given id. */
  interestfor?: string;
};
